import { useLingui } from '@lingui/react/macro';
import { type FC, useEffect, useRef } from 'react';
import { useLocalStorage } from 'react-use';
import { WORKSPACE } from '@/base/constants';
import { useToast } from '@/shared/Toast';
import { useNotificationContext } from './NotificationProvider';

type DismissalData = {
  dismissed: boolean;
};

const STORAGE_KEY = 'notification-dialog-dismissed';

export const EnableNotificationToast: FC = () => {
  const { t } = useLingui();
  const { oneSignal } = useNotificationContext();
  const [dismissalData, setDismissalData] = useLocalStorage<DismissalData>(
    STORAGE_KEY,
    { dismissed: false },
  );

  const toast = useToast();

  const toastId = useRef<string | null>(null);

  useEffect(() => {
    // Only show notification prompt when user is on /app routes
    if (WORKSPACE !== 'app') return;

    const handleConfirm = async () => {
      if (oneSignal) {
        await oneSignal.enableNotifications();
      }
      handleDismiss();
    };

    const handleDismiss = () => {
      setDismissalData({
        dismissed: true,
      });
      if (toastId.current) toast.remove(toastId.current);
    };
    const checkAndShowDialog = async () => {
      if (!oneSignal || toastId.current) return;

      await oneSignal.init();
      const status = oneSignal.getPushStatus();

      if (
        status === 'ok' ||
        status === 'not-supported' ||
        dismissalData?.dismissed
      )
        return;

      toastId.current = toast.add({
        title: t`Enable Browser Notifications`,
        message: (
          <>
            {t`Stay updated with important alerts, account activity, and trading updates.`}
            <button
              className="underline cursor-pointer text-sm text-content-link ps-2"
              onClick={handleConfirm}
              type="button"
            >
              {t`Enable now`}
            </button>
          </>
        ),
        timeout: 20_000,
      });
    };
    const tm = setTimeout(() => {
      checkAndShowDialog();
    }, 3000);
    return () => clearTimeout(tm);
  }, [oneSignal, dismissalData, t, toast.add, setDismissalData, toast.remove]);
  return null;
};
